import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const hasToken = localStorage.getItem("welp_token");
    if (!hasToken && !document.cookie.includes("session_token")) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const u = await api.me();
      setUser(u);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const loginWithToken = (token, u) => {
    localStorage.setItem("welp_token", token);
    setUser(u);
  };

  const logout = async () => {
    try {
      await api.logout();
    } catch (e) {
      // Network/server logout failed — local state will still be cleared below.
      // Suppressed in production; surface in dev for debugging.
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("Logout request failed (clearing local state anyway):", e?.message || e);
      }
    }
    localStorage.removeItem("welp_token");
    setUser(null);
  };

  const value = useMemo(
    () => ({ user, loading, setUser, refresh, loginWithToken, logout }),
    [user, loading, refresh]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
